import { css } from 'lit';

// these styles can be imported from any component
// for an example of how to use this, check /pages/about-about.ts
export const styles = css`
  :host {
    --app-header-height: 56px;
    color: var(--app-text, var(--sl-color-neutral-900));
  }

  main {
    margin-top: var(--app-header-height);
    padding: 0.75rem;
  }

  sl-card {
    --border-color: var(--app-border, var(--sl-color-neutral-200));
    --border-radius: var(--sl-border-radius-large);
    --padding: 1.25rem;
    box-shadow: var(--app-shadow-sm);
  }

  @media (min-width: 1000px) {
    sl-card {
      max-width: 70vw;
    }
  }

  h1,
  h2,
  h3 {
    font-family: var(--font-display, var(--sl-font-sans));
    letter-spacing: -0.015em;
  }

  /*
   * Full-height table-page layout. Pages set their host to flex
   * column with height: 100% so the shell fills the app-shell main;
   * the header + filter row stay anchored at the top, the
   * data-table (with fill-height attribute) takes the remaining
   * space and scrolls internally. Class also provided for any
   * wrapper that wants the same pattern without owning :host.
   */
  .list-page-shell {
    display: flex;
    flex-direction: column;
    height: 100%;
    min-height: 0;
    gap: 12px;
  }
  .list-page-shell > header,
  .list-page-shell > .list-page-filters,
  .list-page-shell > sl-alert {
    flex: none;
  }
  .list-page-shell > app-data-table {
    flex: 1 1 auto;
    min-height: 0;
  }

  /* === Dialog: full-page-on-phone utility ===
   *
   * Apply class="full-page-on-phone" to any <sl-dialog> that should
   * become an edge-to-edge page overlay on small screens (<= 720px),
   * with a sticky header + footer so action buttons don't disappear
   * when the body scrolls. On larger screens the dialog renders
   * normally (centered card).
   *
   * Why: dialogs on phones lose half the screen to chrome + offer a
   * tap-anywhere-outside dismiss that mis-fires on small targets.
   * A page-shaped overlay reads as a route push (back button works,
   * full width, full height) without the URL actually changing — so
   * stateful flows that don't deserve a new route (multi-step
   * picker, confirm-with-options) can still feel right on phone.
   *
   * Each consuming component just slaps the class on its <sl-dialog>;
   * this style block (loaded via shared-styles) does the rest. Style
   * is scoped to the host's shadow root so we can use ::part() to
   * reach into Shoelace's dialog internals.
   */
  @media (max-width: 720px) {
    /* Shoelace's dialog base (part="base") is a fixed, full-viewport flex box
     * that vertically CENTRES the panel. On iOS that base is sized to the tall
     * (layout) viewport while the panel below is sized to the visible (dvh)
     * viewport, so centring the shorter panel leaves a symmetric gap above the
     * header. Top-align it: a full-page-on-phone dialog is a page, not a card —
     * it starts at the top edge and fills down. */
    sl-dialog.full-page-on-phone::part(base) {
      align-items: flex-start;
    }
    sl-dialog.full-page-on-phone::part(panel) {
      max-width: 100vw;
      /* dvh (dynamic viewport height), NOT vh: on iOS Safari 100vh is the
       * *tallest* viewport (toolbars retracted), so a 100vh panel is taller
       * than what's actually visible — the footer (action buttons) would land
       * below the fold, behind the browser's bottom toolbar. dvh tracks the
       * currently visible area, so the panel always fits. vh kept as a fallback
       * for the rare engine without dvh. */
      max-height: 100vh;
      max-height: 100dvh;
      width: 100vw;
      height: 100vh;
      height: 100dvh;
      border-radius: 0;
      margin: 0;
      display: flex;
      flex-direction: column;
    }
    sl-dialog.full-page-on-phone::part(header) {
      position: sticky;
      top: 0;
      background: var(--sl-panel-background-color);
      z-index: 1;
      flex: none;
      /* Clear the notch / status bar when the PWA runs full-bleed. env() is 0
       * on devices without an inset, so this is a no-op elsewhere. Requires
       * viewport-fit=cover (set in index.html). */
      padding-top: calc(var(--sl-spacing-large) + env(safe-area-inset-top));
    }
    sl-dialog.full-page-on-phone::part(body) {
      flex: 1 1 auto;
      overflow-y: auto;
      overscroll-behavior: contain;
    }
    sl-dialog.full-page-on-phone::part(footer) {
      position: sticky;
      bottom: 0;
      background: var(--sl-panel-background-color);
      border-top: 1px solid var(--sl-color-neutral-200);
      flex: none;
      /* Keep the action buttons above the home indicator / bottom toolbar. */
      padding-bottom: calc(
        var(--sl-spacing-large) + env(safe-area-inset-bottom)
      );
    }
  }
`;
